import { CircularProgress } from '@mui/material';
import { walletHistoryResponse } from 'TYPES/api.body';
import { COMMONTYPES } from 'TYPES/event.types';
import { CashIcon, ConversionIcon, WalletSmallIcon } from 'assets/Icons';
import { ConvertPointModal, WalletCard, WalletHistoryMessage } from 'components/molecules';
import FundMadinaWallet from 'components/molecules/m-fund-madina-wallet';
import renderIcon from 'icon';
import QUERY_KEYS from 'network/config/queryKeys';
import { getWalletBalance, getWalletHistories } from 'network/services/wallet';
import { useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { UserWalletCon } from './styles';

export const UserWallet = () => {
  const navigate = useNavigate();
  const [curr, setcurr] = useState<number>(0);
  const [isPoint, setIsPoint] = useState(false);

  // Wallet balance & points - force refetch on every navigation
  const {
    data,
    isLoading: isBalanceLoading,
  } = useQuery(
    QUERY_KEYS.WALLET_BALANCE,
    getWalletBalance,
    {
      staleTime: 0,
      refetchOnMount: 'always',         // ← Key fix: always fetch fresh on mount/navigation
      refetchOnWindowFocus: false,
    }
  );

  const walletBalance = data?.data?.data || null;

  // Wallet history - also force refetch on navigation
  const {
    data: walletHistoryData,
    isLoading: isHistoryLoading,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ['wallet_histories', curr],
    queryFn: () => getWalletHistories(curr),
    staleTime: 0,
    refetchOnMount: 'always',           // ← Key fix: always fetch fresh on mount/navigation
    refetchOnWindowFocus: false,
  });

  const walletData: walletHistoryResponse[] = walletHistoryData?.data?.data || [];
  const totalPages = walletHistoryData?.data?.totalPages ?? 0;

  const showModal = (e: COMMONTYPES['divClick']) => {
    e.stopPropagation();
    setIsPoint(true);
  };

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
        {renderIcon(IoArrowBackCircleSharp, {
          size: 24,
          className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
          onClick: () => navigate('/account'),
        })}
        <span
          className="font-semibold"
          style={{ color: '#222', cursor: 'pointer' }}
          onClick={() => navigate('/account')}
        >
          Account
        </span>
        <span>›</span>
        <span className="font-semibold" style={{ color: '#222' }}>
          Wallet
        </span>
      </div>

      <UserWalletCon>
        <div className="min-h-screen bg-gray-50 p-3 sm:p-4 lg:p-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Wallet</h2>
              <div className="w-full sm:w-auto">
                <FundMadinaWallet />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6">
            <div className="col-span-1">
              <WalletCard
                label="Wallet Balance"
                Icon={WalletSmallIcon}
                amount={
                  isBalanceLoading
                    ? 'Loading...'
                    : `₦${walletBalance?.balance?.toFixed(2) ?? '0.00'}`
                }
              />
            </div>

            <div className="col-span-1">
              <WalletCard
                label="Available Points"
                Icon={CashIcon}
                amount={
                  isBalanceLoading
                    ? 'Loading...'
                    : `${walletBalance?.points?.toLocaleString() ?? '0'}`
                }
              />
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <WalletCard
                label="Convert Points to Cash"
                Icon={ConversionIcon}
                onClick={showModal}
              />
            </div>
          </div>

          {/* Wallet History */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Wallet History</h3>
            </div>

            <div className="p-4 sm:p-6">
              {(isHistoryLoading || !isFetchedAfterMount) && (
                <div className="flex justify-center items-center py-8 sm:py-12">
                  <div className="text-center">
                    <CircularProgress size={30} />
                    <p className="mt-3 text-sm text-gray-500">Loading history...</p>
                  </div>
                </div>
              )}

              {!isHistoryLoading && isFetchedAfterMount && walletData.length > 0 && (
                <div className="space-y-3 sm:space-y-4">
                  <WalletHistoryMessage
                    data={walletData}
                    setvalue={setcurr}
                    value={curr}
                    totalpages={totalPages}
                  />
                </div>
              )}

              {!isHistoryLoading && isFetchedAfterMount && walletData.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-1">No Transaction History</h4>
                  <p className="text-gray-500 text-sm">Your wallet transactions will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Convert Points Modal */}
          {isPoint && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <ConvertPointModal setIsPoint={setIsPoint} />
              </div>
            </div>
          )}
        </div>
      </UserWalletCon>
    </>
  );
};